/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { useFlightContext } from "hooks/flight/useFlightContext";
import { formatPrice } from "helpers/common";
import useApiClient from "hooks/useApiClient";
import noImage from '../../assets/img/no-image.jpg'


const AirlinePriceComparison = ({ flights, onSelectAirline, selectedAirlines = [] }) => {
  const { availabilityResults } = useFlightContext();
  const { urls } = useApiClient();
  const airlineListRef = useRef(null);
  const [bestPriceByAirline, setBestPriceByAirline] = useState({});

  const extractNumericPrice = (flight) => {
    const candidates = [
      flight?.totalPrice,
      flight?.price,
      flight?.recommendation?.price?.detailed_price?.total,
      flight?.recommendation?.price?.total,
    ];

    for (const value of candidates) {
      const numericValue = typeof value === "string" ? parseFloat(value) : value;
      if (typeof numericValue === "number" && !Number.isNaN(numericValue)) {
        return numericValue;
      }
    }
    return undefined;
  };

  // Get all unique airlines from the complete dataset
  const allAirlines = useMemo(() => {
    const airlines = new Set();
    const sourceFlights = availabilityResults?.allFlightCombinations ?? [];
    
    sourceFlights.forEach((flight) => {
      const airline = flight?.validatingCarrier || 
                     flight?.airline || 
                     flight?.bounds?.[0]?.airlines?.[0];
      if (airline) {
        airlines.add(airline);
      }
    });
    
    return Array.from(airlines);
  }, [availabilityResults?.allFlightCombinations]);

  // Calculate best price for each airline from current flights
  useEffect(() => {
    const sourceFlights = flights ?? availabilityResults?.allFlightCombinations;
    if (!sourceFlights) {
      setBestPriceByAirline({});
      return;
    }

    const pricesByAirline = {};
    
    // Initialize all known airlines with null price
    allAirlines.forEach(airline => {
      pricesByAirline[airline] = null;
    });
    
    // Update with actual prices from current flights
    sourceFlights.forEach((flight) => {
      const airline = flight?.validatingCarrier || 
                     flight?.airline || 
                     flight?.bounds?.[0]?.airlines?.[0];

      if (!airline) return;

      const price = extractNumericPrice(flight);
      if (price === undefined) return;

      if (pricesByAirline[airline] === null || price < pricesByAirline[airline]) {
        pricesByAirline[airline] = price;
      }
    });

    setBestPriceByAirline(pricesByAirline);
  }, []);

  const bestPriceEntries = useMemo(() => {
    // Convert to array of [airline, price] and sort by price
    return Object.entries(bestPriceByAirline)
      .sort(([, priceA], [, priceB]) => 
        (priceA === null ? Infinity : priceA) - (priceB === null ? Infinity : priceB)
      );
  }, [bestPriceByAirline]);

  const scrollLeft = () =>
    airlineListRef.current?.scrollBy({ left: -220, behavior: "smooth" });

  const scrollRight = () =>
    airlineListRef.current?.scrollBy({ left: 220, behavior: "smooth" });

  if (!allAirlines.length) return null;

  return (
    <div className="mb-4 px-2 px-md-3">
      {bestPriceEntries.length > 3 && (
        <div className="d-flex gap-2 mb-3">
          {[["‹", scrollLeft], ["›", scrollRight]].map(([icon, action]) => (
            <button
              key={icon}
              onClick={action}
              className="btn p-0 d-flex align-items-center justify-content-center"
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                border: "1px solid rgba(0,0,0,0.08)",
                fontSize: 20,
                fontWeight: 600,
                boxShadow: "0 3px 10px rgba(0,0,0,0.12)",
              }}
            >
              {icon}
            </button>
          ))}
        </div>
      )}

      <div
        ref={airlineListRef}
        className="d-flex gap-3 overflow-auto pb-1"
        style={{ scrollBehavior: "smooth" }}
      >
        {allAirlines.map((airline) => {
          const price = bestPriceByAirline[airline] || null;
          const selected = selectedAirlines.includes(airline);

          return (
            <div
            key={airline}
            onClick={() => onSelectAirline?.(airline)}
            className="d-flex align-items-center"
            style={{
                minWidth: window.innerWidth <= 768 ? 180 : 200,
                padding: "12px 16px",
                borderRadius: 8,
                cursor: "pointer",
                background: "#fff",
                border: selected ? "2px solid var(--main-color)" : "1px solid #e0e0e0",
                transition: "all 0.2s ease",
            }}
            >
            <div className="d-flex align-items-center gap-3 w-100">
                <img
                src={`${process.env.REACT_APP_STATIC_URL}/${urls?.flight?.airlines}/${airline}.png`}
                alt={airline}
                style={{ 
                    width: 40, 
                    height: 40, 
                    objectFit: "contain",
                    filter: selected ? "none" : "opacity(0.8)"
                }}
                onError={(e) => (e.target.src = noImage)}
                />

                <div className="d-flex flex-column">
                <span
                    style={{
                    fontSize: 14,
                    color: selected ? "var(--main-color)" : "#666",
                    fontWeight: selected ? 600 : 500,
                    }}
                >
                    {availabilityResults?.airlineDictionary?.[airline]?.name || airline}
                </span>
                <span
                    style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: selected ? "var(--main-color)" : price === null ? "#999" : "#333",
                    }}
                >
                    {price === null ? 'N/A' : formatPrice(price)}
                </span>
                </div>
            </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AirlinePriceComparison;
